import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader, Plus, Target, ChevronRight, ChevronLeft, Award, AlertTriangle, Home } from 'lucide-react';
import axios from 'axios';
import GoldenGooseResult from '@/components/GoldenGooseResult';
import { collection, addDoc, query, where, orderBy, getDocs } from "firebase/firestore";
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

export default function GoldenGoose() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [referralRequests, setReferralRequests] = useState([]);
  const [analysisData, setAnalysisData] = useState(null);
  const [activeView, setActiveView] = useState('form'); // 'form', 'analysis', 'requests'
  const [formData, setFormData] = useState({
    targetIndustry: '',
    specificCompany: '',
    contactTitle: '',
    painPoints: '',
    valueProposition: ''
  });

  // Load previous attempts from Firestore on component mount
  useEffect(() => {
    fetchGoldenGooseAttempts();
  }, []);

  const fetchGoldenGooseAttempts = async () => {
    setIsFetching(true);
    setError(null);
    
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log("No user logged in, skipping fetch");
        setIsFetching(false);
        return;
      }

      const q = query(
        collection(db, "goldenGooseAttempts"),
        where("userId", "==", user.uid),
        orderBy("timestamp", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const attempts = [];
      
      querySnapshot.forEach((doc) => {
        attempts.push({ 
          id: doc.id, 
          ...doc.data(),
          // Convert Firebase timestamp to JS Date for local display
          createdAt: doc.data().timestamp?.toDate().toISOString() 
        });
      });
      
      setReferralRequests(attempts);
      console.log(`Loaded ${attempts.length} Golden Goose attempts from Firestore`);
    } catch (error) {
      console.error("Error fetching Golden Goose attempts:", error);
      setError("Failed to load previous attempts. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  const saveToFirestore = async (formData, analysisData) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log("No user logged in, skipping save to Firestore");
        return null;
      }

      const docRef = await addDoc(collection(db, "goldenGooseAttempts"), {
        userId: user.uid,
        timestamp: new Date(),
        targetIndustry: formData.targetIndustry,
        specificCompany: formData.specificCompany || "",
        contactTitle: formData.contactTitle,
        painPoints: formData.painPoints,
        valueProposition: formData.valueProposition,
        analysisData: analysisData
      });
      
      console.log("Golden Goose attempt saved with ID:", docRef.id);
      return docRef.id;
    } catch (error) {
      console.error("Error saving to Firestore:", error);
      throw new Error("Failed to save analysis. Please try again.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      let apiBaseUrl = 'https://bni-backend-6698076432.us-central1.run.app';
      
      const apiUrl = `${apiBaseUrl}/api/analyze-golden-goose`;
      console.log('Calling backend API at:', apiUrl);
      
      // Ensure all required fields are present
      const requestData = {
        targetIndustry: formData.targetIndustry || '',
        specificCompany: formData.specificCompany || '',
        contactTitle: formData.contactTitle || '',
        painPoints: formData.painPoints || '',
        valueProposition: formData.valueProposition || ''
      };
      
      console.log('With request data:', requestData);
      
      const response = await axios.post(
        apiUrl,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Received response:', response.data);
      setAnalysisData(response.data);
      
      // Save to Firestore
      await saveToFirestore(formData, response.data);
      
      // Save the request with the analysis locally as well
      const newRequest = {
        id: Date.now(),
        ...formData,
        analysis: response.data,
        status: 'Completed',
        createdAt: new Date().toISOString()
      };
      
      setReferralRequests(prev => [newRequest, ...prev]);
      
      // Switch to analysis view
      setActiveView('analysis');
    } catch (error) {
      console.error('Error analyzing Golden Goose request:', error);
      // Log more detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
        setError(`Server error: ${error.response.status} - ${error.response.data.error || 'Unknown error'}`);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
        setError('No response from server. Please check your connection and try again.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
        setError(`Error: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      targetIndustry: '',
      specificCompany: '',
      contactTitle: '',
      painPoints: '',
      valueProposition: ''
    });
  };

  const handleBackToForm = () => {
    setActiveView('form');
    resetForm();
  };

  const goToRequests = () => {
    setActiveView('requests');
  };

  // Function to test backend connection
  const testBackendConnection = async () => {
    setError(null);
    
    let apiBaseUrl = 'https://bni-backend-6698076432.us-central1.run.app';
    
    try {
      // Try to ping the backend
      const response = await axios.get(`${apiBaseUrl}/api/carriers`);
      alert(`Backend connection successful! Received ${response.data.length} carriers.`);
    } catch (error) {
      console.error('Backend connection test failed:', error);
      if (error.response) {
        setError(`Server responded with status: ${error.response.status}`);
      } else if (error.request) {
        setError('No response from server. Backend might be down or CORS issues.');
      } else {
        setError(`Request error: ${error.message}`);
      }
    }
  };

  // Function to navigate back to dashboard
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  // If we have analysis data to show
  if (activeView === 'analysis' && analysisData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center">
              <Button 
                onClick={() => setActiveView('form')}
                variant="outline"
                size="sm"
                className="mr-4 bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Form
              </Button>
              <h1 className="text-4xl font-bold text-gray-900">{analysisData.visualElements?.displayTitle || "Golden Goose Analysis"}</h1>
            </div>
            <Button
              onClick={goToDashboard}
              variant="outline"
              size="sm"
              className="flex items-center"
            >
              <Home className="h-4 w-4 mr-1" />
              Dashboard
            </Button>
          </div>
          
          <GoldenGooseResult analysisData={analysisData} />
          
          <div className="mt-8 flex justify-between">
            <Button 
              onClick={handleBackToForm} 
              variant="outline"
              className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
            >
              <Plus className="mr-2 h-4 w-4" /> New Analysis
            </Button>
            <Button 
              onClick={goToRequests} 
              variant="outline"
              className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
            >
              View All Analyses <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show all saved requests
  if (activeView === 'requests') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
        <div className="max-w-6xl mx-auto">
          <header className="mb-8 flex justify-between items-center">
            <div className="flex items-center">
              <Button 
                onClick={() => setActiveView('form')}
                variant="outline"
                size="sm"
                className="mr-4 bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <h1 className="text-4xl font-bold text-gray-900">Golden Goose Requests</h1>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={goToDashboard}
                variant="outline"
                size="sm"
                className="mr-2"
              >
                <Home className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <Button 
                onClick={() => fetchGoldenGooseAttempts()}
                variant="outline"
                size="sm"
                className="mr-2"
                disabled={isFetching}
              >
                {isFetching ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" /> 
                    Loading...
                  </>
                ) : (
                  <>
                    <Loader className="mr-2 h-4 w-4" /> 
                    Refresh
                  </>
                )}
              </Button>
              <Button 
                onClick={() => setActiveView('form')}
                variant="outline"
                size="sm"
                className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
              >
                <Plus className="mr-2 h-4 w-4" /> 
                New Request
              </Button>
            </div>
          </header>
          
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-md">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                <div>
                  <h3 className="text-red-800 font-semibold">Error</h3>
                  <p className="text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-6">
            {isFetching && referralRequests.length === 0 ? (
              <Card>
                <CardContent className="text-center py-8">
                  <Loader className="h-8 w-8 animate-spin mx-auto mb-4 text-yellow-600" />
                  <p className="text-gray-500">Loading your Golden Goose analyses...</p>
                </CardContent>
              </Card>
            ) : referralRequests.length > 0 ? (
              referralRequests.map(request => (
                <Card key={request.id} className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="flex justify-between items-center">
                      <span>{request.targetIndustry}</span>
                      {request.analysisData && request.analysisData.targetAssessment && (
                        <span className="text-sm font-medium bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                          Score: {request.analysisData.targetAssessment.score}%
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {request.specificCompany && (
                        <div>
                          <Label>Target Company</Label>
                          <p className="text-gray-600">{request.specificCompany}</p>
                        </div>
                      )}
                      <div>
                        <Label>Looking for</Label>
                        <p className="text-gray-600">{request.contactTitle}</p>
                      </div>
                      
                      <div className="border-t pt-4 mt-4">
                        <div className="text-sm text-gray-700 mb-2">
                          {request.analysisData && request.analysisData.isGoldenGoose ? (
                            <div className="flex items-center text-green-600">
                              <Award className="h-4 w-4 mr-1" />
                              <span>True Golden Goose</span>
                            </div>
                          ) : (
                            <div className="flex items-center text-red-600">
                              <AlertTriangle className="h-4 w-4 mr-1" />
                              <span>Not a Golden Goose</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex justify-between">
                          <div className="text-sm text-gray-500">
                            Created: {new Date(request.createdAt).toLocaleDateString()}
                          </div>
                          {request.analysisData && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setAnalysisData(request.analysisData);
                                setActiveView('analysis');
                              }}
                            >
                              View Analysis
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="text-center py-8">
                  <p className="text-gray-500">No referral requests yet. Create one to get started!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default to the form view
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Golden Goose Generator</h1>
          <div className="flex gap-2">
            <Button
              onClick={goToDashboard}
              variant="outline"
              size="sm"
            >
              <Home className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            {referralRequests.length > 0 && (
              <Button 
                onClick={goToRequests}
                variant="outline" 
                size="sm"
              >
                <ChevronRight className="mr-2 h-4 w-4" />
                View Previous Analyses
              </Button>
            )}
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-md">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
              <div>
                <h3 className="text-red-800 font-semibold">Error</h3>
                <p className="text-red-700 mt-1">{error}</p>
                <p className="text-red-600 mt-2 text-sm">
                  Please try again or contact support if the issue persists.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-6 w-6 text-yellow-600" />
              <span>New Golden Goose Analysis</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="targetIndustry">Target Industry</Label>
                <Input
                  id="targetIndustry"
                  name="targetIndustry"
                  value={formData.targetIndustry}
                  onChange={handleInputChange}
                  placeholder="e.g., Healthcare, Technology, Real Estate"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="specificCompany">Specific Company (Optional)</Label>
                <Input
                  id="specificCompany"
                  name="specificCompany"
                  value={formData.specificCompany}
                  onChange={handleInputChange}
                  placeholder="e.g., Acme Corp"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactTitle">Contact Title/Position</Label>
                <Input
                  id="contactTitle"
                  name="contactTitle"
                  value={formData.contactTitle}
                  onChange={handleInputChange}
                  placeholder="e.g., Chief Financial Officer, HR Director"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="painPoints">Pain Points</Label>
                <Textarea
                  id="painPoints"
                  name="painPoints"
                  value={formData.painPoints}
                  onChange={handleInputChange}
                  placeholder="Describe the problems or challenges your ideal referral might be facing..."
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="valueProposition">Value Proposition</Label>
                <Textarea
                  id="valueProposition"
                  name="valueProposition"
                  value={formData.valueProposition}
                  onChange={handleInputChange}
                  placeholder="Describe how you can help solve their problems..."
                  rows={3}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button 
                  type="submit" 
                  variant="outline"
                  className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200 md:col-span-2 mx-auto w-full md:w-auto px-8"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader className="mr-2 h-4 w-4 animate-spin" /> 
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Award className="mr-2 h-4 w-4" /> 
                      Analyze Golden Goose Potential
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 